import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Logo from 'assets/images/logo-white.png';
import cn from 'classnames';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import MetaTags from 'react-meta-tags';
import { Link, withRouter } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import * as Yup from 'yup';

import { MCCard, MCField } from 'components/mc';
import TurnstileWidget from 'components/Custom/TurnstileWidget';

import recoveryService from '../../services/recoveryService';

import { t } from '../../i18n';
import s from './scss/AccountRecovery.module.scss';
import authStyles from './scss/AuthPages.module.scss';
import Footer from 'components/VerticalLayout/Footer';

const STEP_INTAKE = 0;
const STEP_VERIFY = 1;
const STEP_DONE = 2;

// Public Turnstile sitekey fallback — used only if the recovery service config
// endpoint is unreachable. A real sitekey is safe to ship to the browser.
const FALLBACK_TURNSTILE_SITEKEY =
  process.env.REACT_APP_TURNSTILE_SITEKEY || '0x4AAAAAADn360bgep20-D_T';

// Reads ?case_ref= from the deep-link a client follows out of a rejection email
// so the SAME case is re-opened (intake forwards it; verify can resume directly).
const readCaseRefFromQuery = (search) => {
  try {
    return new URLSearchParams(search || '').get('case_ref') || '';
  } catch {
    return '';
  }
};

const CheckIcon = () => (
  <svg
    className={s.confirmIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

const StepDots = ({ step }) => (
  <div className={s.steps} aria-hidden="true">
    {[STEP_INTAKE, STEP_VERIFY, STEP_DONE].map((i) => (
      <span key={i} className={cn(s.stepDot, { [s.stepDotActive]: i <= step })} />
    ))}
  </div>
);

StepDots.propTypes = { step: PropTypes.number.isRequired };

const AccountRecovery = (props) => {
  const deepLinkCaseRef = useMemo(
    () => readCaseRefFromQuery(props.location?.search),
    [props.location?.search],
  );

  // If the user arrives with a ?case_ref=, resume at the verify step so they can
  // re-enter the code; otherwise start the intake form.
  const [step, setStep] = useState(deepLinkCaseRef ? STEP_VERIFY : STEP_INTAKE);
  const [caseRef, setCaseRef] = useState(deepLinkCaseRef || '');
  const [intakeError, setIntakeError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [intakeSubmitted, setIntakeSubmitted] = useState(false);

  // Captcha (Cloudflare Turnstile). Policy comes from the recovery service at
  // mount; until it resolves we assume captcha is enabled so submit stays gated.
  const [captchaEnabled, setCaptchaEnabled] = useState(true);
  const [captchaSitekey, setCaptchaSitekey] = useState(FALLBACK_TURNSTILE_SITEKEY);
  const [captchaToken, setCaptchaToken] = useState('');

  // Liveness waiting screen (STEP_DONE): the hosted AMLBot URL the user opens, and
  // the coarse status we resolve on their return ('pending'|'in_review'|'failed').
  const [verificationUrl, setVerificationUrl] = useState('');
  const [doneStatus, setDoneStatus] = useState('pending');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // Keep state in sync if the deep-link changes (e.g. client edits the URL).
    if (deepLinkCaseRef) {
      setCaseRef(deepLinkCaseRef);
      setStep((prev) => (prev === STEP_INTAKE ? STEP_VERIFY : prev));
    }
  }, [deepLinkCaseRef]);

  useEffect(() => {
    let cancelled = false;
    recoveryService
      .getConfig()
      .then((cfg) => {
        if (cancelled || !cfg) return;
        // Provider is informational; we only render Turnstile here.
        setCaptchaEnabled(cfg.captcha_enabled !== false);
        if (cfg.captcha_sitekey) setCaptchaSitekey(cfg.captcha_sitekey);
      })
      .catch(() => {
        // Config unreachable — fall back to the public sitekey and keep the
        // widget on (fail-closed: never let intake through without a challenge).
        if (!cancelled) {
          setCaptchaEnabled(true);
          setCaptchaSitekey(FALLBACK_TURNSTILE_SITEKEY);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const intakeForm = useFormik({
    enableReinitialize: false,
    initialValues: { claimedOriginalEmail: '', newEmail: '', note: '' },
    validationSchema: Yup.object({
      claimedOriginalEmail: Yup.string()
        .email(t('auth_reset_wrong_email_error'))
        .required(t('common_email_hint')),
      newEmail: Yup.string()
        .email(t('auth_reset_wrong_email_error'))
        .required(t('common_email_hint')),
      note: Yup.string().max(1024),
    }),
    onSubmit: async (values) => {
      // Guard: when captcha is on, a fresh token is mandatory (also enforced by
      // the disabled submit button — this is defence-in-depth).
      if (captchaEnabled && !captchaToken) {
        setIntakeError(t('account_recovery_captcha_required'));
        return;
      }
      setIntakeError('');
      setIntakeLoading(true);
      try {
        const data = await recoveryService.intake({
          claimedOriginalEmail: values.claimedOriginalEmail.trim(),
          newEmail: values.newEmail.trim(),
          note: values.note?.trim() || undefined,
          caseRef: caseRef || undefined,
          captchaToken: captchaEnabled ? captchaToken : undefined,
        });
        // The response is identical whether or not an account matched; we only
        // store the opaque case_ref and advance to the verify step. We NEVER
        // reveal whether the account exists.
        setCaseRef(data?.case_ref || caseRef);
        setIntakeSubmitted(true);
        // Turnstile tokens are single-use — drop it so a stale token can't be
        // reused if the user navigates back to the intake step.
        setCaptchaToken('');
        setStep(STEP_VERIFY);
      } catch (err) {
        setIntakeError(t('account_recovery_generic_error'));
        // Force a new challenge on retry.
        setCaptchaToken('');
      } finally {
        setIntakeLoading(false);
      }
    },
  });

  const verifyForm = useFormik({
    enableReinitialize: false,
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string().required(t('common_code_hint_required')),
    }),
    onSubmit: async (values) => {
      setVerifyError('');
      setVerifyLoading(true);
      try {
        const data = await recoveryService.verifyEmail({
          caseRef,
          code: values.code.replace(/[\s-]/g, ''),
        });
        // Store the hosted liveness URL and open it for the user; the DONE screen
        // then waits for them to finish and checks the result on their return.
        const url = data?.verification_url || '';
        setVerificationUrl(url);
        setDoneStatus('pending');
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        setStep(STEP_DONE);
      } catch (err) {
        // Generic 400 (wrong / expired / unknown case_ref) — never leak which.
        setVerifyError(t('account_recovery_code_error'));
      } finally {
        setVerifyLoading(false);
      }
    },
  });

  // On-demand liveness check — the action-driven path (vs the 5-min server poll).
  // Resolves the coarse status; a transient error keeps us 'pending' to retry.
  const runIdentityCheck = useCallback(async () => {
    if (!caseRef) return;
    setChecking(true);
    try {
      const data = await recoveryService.checkIdentity(caseRef);
      if (data?.status === 'in_review' || data?.status === 'failed') {
        setDoneStatus(data.status);
      }
    } catch {
      // Transient — stay 'pending'; the next check / server backstop will catch it.
    } finally {
      setChecking(false);
    }
  }, [caseRef]);

  // While waiting on the DONE screen, check when the user returns to this tab
  // (they just finished the AMLBot check) and on a short interval — so the result
  // lands in seconds. Auto-polling stops after a few minutes (server backstop
  // covers an abandoned tab).
  useEffect(() => {
    if (step !== STEP_DONE || doneStatus !== 'pending') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') runIdentityCheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', runIdentityCheck);
    const poll = setInterval(runIdentityCheck, 10000);
    const stopAt = setTimeout(() => clearInterval(poll), 180000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', runIdentityCheck);
      clearInterval(poll);
      clearTimeout(stopAt);
    };
  }, [step, doneStatus, runIdentityCheck]);

  const renderIntake = () => (
    <form
      className={s.fields}
      onSubmit={(e) => {
        e.preventDefault();
        intakeForm.handleSubmit();
      }}>
      {intakeError ? <p className={cn(s.alert, s.alertError)}>{intakeError}</p> : null}

      <MCField
        label={t('account_recovery_original_email_label')}
        htmlFor="claimedOriginalEmail"
        error={
          intakeForm.touched.claimedOriginalEmail && intakeForm.errors.claimedOriginalEmail
            ? intakeForm.errors.claimedOriginalEmail
            : null
        }>
        <input
          id="claimedOriginalEmail"
          name="claimedOriginalEmail"
          type="text"
          autoComplete="email"
          className={cn(s.input, {
            [s.invalid]:
              intakeForm.touched.claimedOriginalEmail && intakeForm.errors.claimedOriginalEmail,
          })}
          placeholder={t('common_email_placeholder')}
          value={intakeForm.values.claimedOriginalEmail}
          onChange={intakeForm.handleChange}
          onBlur={intakeForm.handleBlur}
        />
      </MCField>

      <MCField
        label={t('account_recovery_new_email_label')}
        htmlFor="newEmail"
        error={
          intakeForm.touched.newEmail && intakeForm.errors.newEmail
            ? intakeForm.errors.newEmail
            : null
        }>
        <input
          id="newEmail"
          name="newEmail"
          type="text"
          autoComplete="email"
          className={cn(s.input, {
            [s.invalid]: intakeForm.touched.newEmail && intakeForm.errors.newEmail,
          })}
          placeholder={t('common_email_placeholder')}
          value={intakeForm.values.newEmail}
          onChange={intakeForm.handleChange}
          onBlur={intakeForm.handleBlur}
        />
      </MCField>

      <MCField label={t('account_recovery_note_label')} htmlFor="note">
        <textarea
          id="note"
          name="note"
          className={s.textarea}
          placeholder={t('account_recovery_note_placeholder')}
          value={intakeForm.values.note}
          onChange={intakeForm.handleChange}
          onBlur={intakeForm.handleBlur}
        />
      </MCField>

      {captchaEnabled ? (
        <div className={s.captcha}>
          <TurnstileWidget
            sitekey={captchaSitekey}
            theme="dark"
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken('')}
            onError={() => setCaptchaToken('')}
          />
        </div>
      ) : null}

      <div className={s.actions}>
        <button
          className={s.primaryBtn}
          type="submit"
          disabled={intakeLoading || (captchaEnabled && !captchaToken)}>
          {intakeLoading ? (
            <>
              <Spinner className="spinner" /> {t('account_recovery_submit')}
            </>
          ) : (
            t('account_recovery_submit')
          )}
        </button>
      </div>
    </form>
  );

  const renderVerify = () => (
    <form
      className={s.fields}
      onSubmit={(e) => {
        e.preventDefault();
        verifyForm.handleSubmit();
      }}>
      {/* Neutral, identical-for-everyone message. Does NOT confirm the account exists. */}
      {intakeSubmitted ? (
        <p className={cn(s.alert, s.alertNeutral)}>{t('account_recovery_intake_neutral')}</p>
      ) : (
        <p className={s.note}>{t('account_recovery_verify_resume_hint')}</p>
      )}

      {verifyError ? <p className={cn(s.alert, s.alertError)}>{verifyError}</p> : null}

      <MCField
        label={t('account_recovery_code_label')}
        htmlFor="code"
        error={
          verifyForm.touched.code && verifyForm.errors.code ? verifyForm.errors.code : null
        }>
        <input
          id="code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          className={cn(s.input, s.codeInput, {
            [s.invalid]: verifyForm.touched.code && verifyForm.errors.code,
          })}
          placeholder="••••••"
          value={verifyForm.values.code}
          onChange={verifyForm.handleChange}
          onBlur={verifyForm.handleBlur}
        />
      </MCField>

      <div className={s.actions}>
        <button className={s.primaryBtn} type="submit" disabled={verifyLoading}>
          {verifyLoading ? (
            <>
              <Spinner className="spinner" /> {t('account_recovery_verify_submit')}
            </>
          ) : (
            t('account_recovery_verify_submit')
          )}
        </button>
        <button
          type="button"
          className={s.linkBtn}
          onClick={() => {
            setVerifyError('');
            setStep(STEP_INTAKE);
          }}>
          {t('account_recovery_back_to_form')}
        </button>
      </div>
    </form>
  );

  const renderDone = () => {
    // Identity confirmed → request is with the admins; user waits for the email.
    if (doneStatus === 'in_review') {
      return (
        <div className={s.confirm}>
          <CheckIcon />
          <h5 className={s.confirmTitle}>{t('account_recovery_review_title')}</h5>
          <p className={s.note}>{t('account_recovery_review_text')}</p>
          <div className={s.actions} style={{ width: '100%' }}>
            <Link to="/login" className={s.primaryBtn}>
              {t('auth_register_login')}
            </Link>
          </div>
        </div>
      );
    }

    // Liveness did not pass → let them retry the check.
    if (doneStatus === 'failed') {
      return (
        <div className={s.confirm}>
          <h5 className={s.confirmTitle}>{t('account_recovery_kyc_failed_title')}</h5>
          <p className={s.note}>{t('account_recovery_kyc_failed_text')}</p>
          <div className={s.actions} style={{ width: '100%' }}>
            {verificationUrl ? (
              <a
                className={s.primaryBtn}
                href={verificationUrl}
                target="_blank"
                rel="noopener noreferrer">
                {t('account_recovery_open_liveness')}
              </a>
            ) : null}
            <Link to="/login" className={s.linkBtn}>
              {t('auth_register_login')}
            </Link>
          </div>
        </div>
      );
    }

    // Pending: the user is doing (or about to do) the liveness check. We auto-check
    // on their return to this tab + a short poll; the button is an explicit trigger.
    return (
      <div className={s.confirm}>
        <h5 className={s.confirmTitle}>{t('account_recovery_liveness_title')}</h5>
        <p className={s.note}>{t('account_recovery_liveness_text')}</p>
        <div className={s.actions} style={{ width: '100%' }}>
          {verificationUrl ? (
            <a
              className={s.primaryBtn}
              href={verificationUrl}
              target="_blank"
              rel="noopener noreferrer">
              {t('account_recovery_open_liveness')}
            </a>
          ) : null}
          <button
            type="button"
            className={verificationUrl ? s.linkBtn : s.primaryBtn}
            onClick={runIdentityCheck}
            disabled={checking}>
            {checking ? (
              <>
                <Spinner className="spinner" /> {t('account_recovery_check_identity')}
              </>
            ) : (
              t('account_recovery_check_identity')
            )}
          </button>
        </div>
      </div>
    );
  };

  let body = null;
  let subtitle = t('account_recovery_subtitle');
  if (step === STEP_VERIFY) {
    body = renderVerify();
    subtitle = t('account_recovery_verify_subtitle');
  } else if (step === STEP_DONE) {
    body = renderDone();
    subtitle = t('account_recovery_done_subtitle');
  } else {
    body = renderIntake();
  }

  return (
    <React.Fragment>
      <MetaTags>
        <title>{t('account_recovery_meta_title')}</title>
      </MetaTags>
      <div className={cn(authStyles.wrapper, s.scope)}>
        <div className={authStyles.formWrapper}>
          <img src={Logo} alt="logo" className={authStyles.logo} />
          <div>
            <h4 className={authStyles.title}>{t('account_recovery_title')}</h4>
            <p className={authStyles.subtitle}>{subtitle}</p>
          </div>

          <MCCard pad="lg" className={s.card}>
            <StepDots step={step} />
            {body}
          </MCCard>

          {step !== STEP_DONE && (
            <p className={authStyles.bottomText}>
              {t('account_recovery_login_hint')}{' '}
              <Link to="/login" className={authStyles.bottomTextLink}>
                {t('auth_register_login')}
              </Link>
            </p>
          )}
        </div>
        <Footer authFooter />
      </div>
    </React.Fragment>
  );
};

AccountRecovery.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
};

export default withRouter(AccountRecovery);
